import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Row, Col, FormText } from 'reactstrap';
import { isNumber, Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IUser } from 'app/shared/model/user.model';
import { getUsers } from 'app/modules/administration/user-management/user-management.reducer';
import { IProductDeliveryStatus } from 'app/shared/model/product-delivery-status.model';
import { getEntities as getProductDeliveryStatuses } from 'app/entities/product-delivery-status/product-delivery-status.reducer';
import { ITransactionDetails } from 'app/shared/model/transaction-details.model';
import { getEntities as getTransactionDetails } from 'app/entities/transaction-details/transaction-details.reducer';
import { IUserPurchasedSupplements } from 'app/shared/model/user-purchased-supplements.model';
import { getEntity, updateEntity, createEntity, reset } from './user-purchased-supplements.reducer';

export const UserPurchasedSupplementsUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const users = useAppSelector(state => state.userManagement.users);
  const productDeliveryStatuses = useAppSelector(state => state.productDeliveryStatus.entities);
  const transactionDetails = useAppSelector(state => state.transactionDetails.entities);
  const userPurchasedSupplementsEntity = useAppSelector(state => state.userPurchasedSupplements.entity);
  const loading = useAppSelector(state => state.userPurchasedSupplements.loading);
  const updating = useAppSelector(state => state.userPurchasedSupplements.updating);
  const updateSuccess = useAppSelector(state => state.userPurchasedSupplements.updateSuccess);

  const handleClose = () => {
    navigate('/user-purchased-supplements' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getUsers({}));
    dispatch(getProductDeliveryStatuses({}));
    dispatch(getTransactionDetails({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    values.supplementSentDate = convertDateTimeToServer(values.supplementSentDate);
    values.supplementDeliveredDate = convertDateTimeToServer(values.supplementDeliveredDate);

    const entity = {
      ...userPurchasedSupplementsEntity,
      ...values,
      user: users.find(it => it.id.toString() === values.user.toString()),
      productDeliveryStatus: productDeliveryStatuses.find(it => it.id.toString() === values.productDeliveryStatus.toString()),
      transactionDetails: transactionDetails.find(it => it.id.toString() === values.transactionDetails.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          supplementSentDate: displayDefaultDateTime(),
          supplementDeliveredDate: displayDefaultDateTime(),
        }
      : {
          ...userPurchasedSupplementsEntity,
          supplementSentDate: convertDateTimeFromServer(userPurchasedSupplementsEntity.supplementSentDate),
          supplementDeliveredDate: convertDateTimeFromServer(userPurchasedSupplementsEntity.supplementDeliveredDate),
          user: userPurchasedSupplementsEntity?.user?.id,
          productDeliveryStatus: userPurchasedSupplementsEntity?.productDeliveryStatus?.id,
          transactionDetails: userPurchasedSupplementsEntity?.transactionDetails?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2
            id="fitengineerBackendV2App.userPurchasedSupplements.home.createOrEditLabel"
            data-cy="UserPurchasedSupplementsCreateUpdateHeading"
          >
            <Translate contentKey="fitengineerBackendV2App.userPurchasedSupplements.home.createOrEditLabel">
              Create or edit a UserPurchasedSupplements
            </Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="user-purchased-supplements-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('fitengineerBackendV2App.userPurchasedSupplements.supplementSentDate')}
                id="user-purchased-supplements-supplementSentDate"
                name="supplementSentDate"
                data-cy="supplementSentDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('fitengineerBackendV2App.userPurchasedSupplements.supplementDeliveredDate')}
                id="user-purchased-supplements-supplementDeliveredDate"
                name="supplementDeliveredDate"
                data-cy="supplementDeliveredDate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                id="user-purchased-supplements-user"
                name="user"
                data-cy="user"
                label={translate('fitengineerBackendV2App.userPurchasedSupplements.user')}
                type="select"
                required
              >
                <option value="" key="0" />
                {users
                  ? users.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.login}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="user-purchased-supplements-productDeliveryStatus"
                name="productDeliveryStatus"
                data-cy="productDeliveryStatus"
                label={translate('fitengineerBackendV2App.userPurchasedSupplements.productDeliveryStatus')}
                type="select"
                required
              >
                <option value="" key="0" />
                {productDeliveryStatuses
                  ? productDeliveryStatuses.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.status}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="user-purchased-supplements-transactionDetails"
                name="transactionDetails"
                data-cy="transactionDetails"
                label={translate('fitengineerBackendV2App.userPurchasedSupplements.transactionDetails')}
                type="select"
                required
              >
                <option value="" key="0" />
                {transactionDetails
                  ? transactionDetails.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/user-purchased-supplements" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default UserPurchasedSupplementsUpdate;
